import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  deletePatientAgenda,
  deletePatientNotes,
  deletePatientNotification,
  deletePatientRelation,
  delPatientHealthData,
  delPatientHealthDataByPath,
  delPatientHealthService,
  delPatientInfoDataByPath,
  delPatientRules,
  getHistoricServiceState,
  getHistoricUserDevices,
  getPatient,
  getPatientActions,
  getPatientAgenda,
  getPatientAgendaEvents,
  getPatientHealthData,
  getPatientHealthDataByPath,
  getPatientEffectiveMeasureRules,
  getPatientHealthInsurance,
  getPatientHealthService,
  getPatientInfoDataByPath,
  getPatientLastActions,
  getPatientLastLocation,
  getPatientMeasures,
  getPatientMeasuresGrouped,
  getPatientMeasuresDate,
  getPatientMeasuresByType,
  getPatientMeasuresDateGrouped,
  getPatientNotes,
  getPatientNotesAllTypes,
  getPatientNotifications,
  getPatientPersonalData,
  getPatientRelations,
  getPatientRules,
  getPatientServiceStateOptions,
  getPatientServiceTypeOptions,
  getPatientServiceExpiration,
  putPatientServiceExpiration,
  getServiceConfiguration,
  getServiceContactWay,
  getServicesState,
  getTermsAndConditionsAgreement,
  getUserHistoric,
  getUserUnreadNotificationsCount,
  getVideoCallClose,
  getVideoCallOpen,
  postAddHistoricServiceStateNote,
  postAddNote,
  postAddPatientAgenda,
  postMeasureNote,
  postPatientActionsSearch,
  postPatientAddMeasure,
  postPatientHealthDataByPath,
  postPatientHealthService,
  postPatientInfoDataByPath,
  postPatientNotification,
  postPatientPersonalData,
  postPatientRules,
  postRelationships,
  postReport,
  postServiceConfiguration,
  postServiceState,
  postServiceType,
  putDeviceUser,
  putPatient,
  putPatientAgenda,
  putPatientHealthData,
  putPatientHealthDataByPath,
  putPatientHealthInsurance,
  putPatientHealthService,
  putPatientInfoDataByPath,
  putPatientNotification,
  putPatientNotificationResponse,
  putPatientNotificationSeen,
  putPatientNotificationSend,
  putPatientPersonalData,
  putPatientRules,
  putRelationships,
  putServiceConfiguration,
  putServiceState,
  putServiceType,
  putUnlinkDeviceUser,
} from "services/api";
import {
  setDelHealthData,
  setUpdateHealthData,
  updatePatienteMeasureNotes,
  updatePersonalData,
} from './userDetailSlice';
import { getDeviceGeneralAsync, getHistoricDeviceUsersAsync } from '../deviceDetail/asyncActions';

const getId = (data: any) =>
  typeof data === 'object' && data !== null && 'id' in data ? data.id : data;

export const getPatientAsync: any | null = createAsyncThunk(
  'userDetail/getPatient',
  async (data: any) => {
    const response = await getPatient(getId(data));
    return response;
  },
);

export const getUserHistoricAsync: any | null = createAsyncThunk(
  'userDetail/getUserHistoric',
  async (data: any) => {
    const response = await getUserHistoric(data);
    return response;
  },
  {
    // Sin id gdp-api solo puede responder 102 («Not found entity User: null»): no se pide.
    condition: (data: any) => !!data?.id,
  },
);

export const getPatientLastLocationAsync: any | null = createAsyncThunk(
  'userDetail/getPatientLastLocation',
  async (data: any) => {
    const response = await getPatientLastLocation(getId(data));
    return response;
  },
);

const buildMeasureTypeLookup = (state: any): Record<string, string> => {
  const items: any[] = state.masters?.measuresTypes?.data ?? [];
  return Object.fromEntries(items.map((t: any) => [t.code, t.label ?? t.code]));
};

const buildMeasureGenericTypeLookup = (state: any): Record<string, string> => {
  const items: any[] = state.masters?.measuresGenericTypes?.data ?? [];
  return Object.fromEntries(items.map((t: any) => [t.code, t.label ?? t.code]));
};

export const getPatientMeasuresAsync: any | null = createAsyncThunk(
  'userDetail/getPatientMeasures',
  async (data: any, { getState }) => {
    const state: any = getState();
    // The user can switch between the grouped-by-wearable view and the traditional
    // flat listing; pick the endpoint accordingly (size = days when grouped, rows
    // when flat — the caller passes the right value).
    const params = { init: data.init, size: data.size, peripheralFilters: data.peripheralFilters, measureTypeFilters: data.measureTypeFilters };
    const response = data.grouped
      ? await getPatientMeasuresGrouped(data.id, params)
      : await getPatientMeasures(data.id, params);
    return { response, grouped: data.grouped, measureTypeLookup: buildMeasureTypeLookup(state) };
  },
);

export const getPatientMeasuresDateAsync: any | null = createAsyncThunk(
  'userDetail/getPatientMeasuresDate',
  async (data: any, { getState }) => {
    const state: any = getState();
    // Grouped vs traditional day-range listing, per the user's choice.
    const params = { fromDate: data.fromDate, toDate: data.toDate };
    const response = data.grouped
      ? await getPatientMeasuresDateGrouped(data.id, params)
      : await getPatientMeasuresDate(data.id, params);
    return {
      response,
      grouped: data.grouped,
      measureTypeLookup: buildMeasureTypeLookup(state),
      measureGenericTypeLookup: buildMeasureGenericTypeLookup(state),
    };
  },
);

export const getPatientActiveAgendaAsync: any | null = createAsyncThunk(
  'userDetail/getPatientActiveAgenda',
  async (data: any) => {
    const response = await getPatientAgenda({ userId: data.id, active: true });
    return response?.data;
  },
);

export const getPatientFinishedAgendaAsync: any | null = createAsyncThunk(
  'userDetail/getPatientFinishedAgenda',
  async (data: any) => {
    const response = await getPatientAgenda({ userId: data.id, active: false });
    return response?.data;
  },
);

export const postAddPatientAgendaAsync: any | null = createAsyncThunk(
  'userDetail/postAddPatientAgenda',
  async ({ data, contextKey }: { data: any; contextKey?: string }, { dispatch }) => {
    const response = await postAddPatientAgenda(data);

    if (response?.status === 200) {
      dispatch(getPatientActiveAgendaAsync({ id: data.userId, contextKey }));
      dispatch(getPatientFinishedAgendaAsync({ id: data.userId, contextKey }));
    }

    return response;
  },
);

export const putPatientAgendaAsync: any | null = createAsyncThunk(
  'userDetail/putPatientAgenda',
  async ({ data, contextKey }: { data: any; contextKey?: string }, { dispatch }) => {
    const response = await putPatientAgenda(data);

    if (response?.status === 200) {
      dispatch(getPatientActiveAgendaAsync({ id: data.userId, contextKey }));
      dispatch(getPatientFinishedAgendaAsync({ id: data.userId, contextKey }));
    }

    return response;
  },
);

export const getPatientAgendaEventsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientAgendaEvents',
  async (data: any) => {
    const response = await getPatientAgendaEvents({
      activityId: data.id,
      onlyFutureEvents: data.onlyFutureEvents,
    });
    return response;
  },
);

export const deletePatientAgendaAsync: any | null = createAsyncThunk(
  'userDetail/deletePatientAgenda',
  async (data: any, { dispatch }) => {
    const response = await deletePatientAgenda(data);

    if (response?.status === 200) {
      dispatch(getPatientActiveAgendaAsync({ id: data.userId, contextKey: data.contextKey }));
      dispatch(getPatientFinishedAgendaAsync({ id: data.userId, contextKey: data.contextKey }));
    }

    return response;
  },
);

export const getRelationsOfUserAsync: any | null = createAsyncThunk(
  'userDetail/getRelationsOfUser',
  async (data: any) => {
    const response = await getPatientRelations(data);
    return response;
  },
);

export const delRelationOfUserAsync: any | null = createAsyncThunk(
  'userDetail/deleteRelationOfUser',
  async (data: any) => {
    const response = await deletePatientRelation(data);
    return response;
  },
);

export const putPatientAsync: any | null = createAsyncThunk(
  'userDetail/putPatient',
  async (data: any, { dispatch }) => {
    const { devices, ...payload } = data; // quitamos devices para el request
    const response = await putPatient(payload);

    if (response?.data?.success) {
      dispatch(updatePersonalData(data)); // mantenemos devices en el store
    }
    return response;
  },
);

export const postAddNoteAsync: any | null = createAsyncThunk(
  'userDetail/postAddNote',
  async (data: any, { dispatch }) => {
    const response = await postAddNote(data);
    response?.data.success && dispatch(getPatientNotesAsync({
      id: data.referenceId,
      init: 0,
      size: 10,
      contextKey: data.contextKey,
    }));
    return response;
  },
);

export const getPatientNotesAsync: any | null = createAsyncThunk(
  'userDetail/getPatientNotes',
  async (data: any) => {
    const response = await getPatientNotes(data);
    return response;
  },
);

export const getPatientNotesAllTypesAsync: any | null = createAsyncThunk(
  'userDetail/getPatientNotesAllTypes',
  async (data: any, { getState }) => {
    const state: any = getState();
    const response = await getPatientNotesAllTypes(data);
    const allResolutions = [
      ...(state.masters.resolutions?.notArchived?.data ?? []),
      ...(state.masters.resolutions?.archived?.data ?? []),
    ];
    const resolutionLookup: Record<string, string> = Object.fromEntries(
      allResolutions.map((r: any) => [r.code, r.label])
    );
    return { ...response, resolutionLookup };
  },
);

export const deletePatientNotesAsync: any | null = createAsyncThunk(
  'userDetail/deletePatientNotes',
  async (data: any, { dispatch }) => {
    const response = await deletePatientNotes(data);
    if (response?.data.success) {
      dispatch(getPatientNotesAsync({
        id: data.referenceId,
        init: 0,
        size: 10,
        contextKey: data.contextKey
      }));
    }
    return response;
  },
);

export const getPatientActionsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientActions',
  async (data: any) => {
    const response = await getPatientActions(data);
    return response;
  },
);

export const postPatientActionsSearchAsync: any | null = createAsyncThunk(
  'userDetail/postPatientActionsSearch',
  async (data: any) => {
    const response = await postPatientActionsSearch(data);
    return response;
  },
);

export const getPatientLastActionsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientLastActions',
  async (data: any) => {
    const response = await getPatientLastActions(data);
    return response;
  },
);

export const getPatientNotificationsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientNotifications',
  async (data: any) => {
    const response = await getPatientNotifications(data);
    return response;
  },
);

export const getUserUnreadNotificationsCountAsync: any | null = createAsyncThunk(
  'userDetail/getUserUnreadNotificationsCount',
  async (data: any) => {
    const response = await getUserUnreadNotificationsCount(data.id);
    return response?.data;
  },
);

export const postPatientNotificationAsync: any | null = createAsyncThunk(
  'userDetail/postPatientNotification',
  async (data: any, { dispatch }) => {
    const response = await postPatientNotification(data);
    if (response?.data.success) {
      dispatch(getPatientNotificationsAsync({
        id: data.patientId,
        init: 0,
        size: 10,
        contextKey: data.contextKey
      }));
    }
    return response;
  },
);

export const putPatientNotificationAsync: any | null = createAsyncThunk(
  'userDetail/putPatientNotification',
  async (data: any, { dispatch }) => {
    const response = await putPatientNotification(data);
    if (response?.data.success) {
      dispatch(getPatientNotificationsAsync({
        id: data.patientId,
        init: 0,
        size: 10,
        contextKey: data.contextKey
      }));
    }
    return response;
  },
);

export const putPatientNotificationSendAsync: any | null = createAsyncThunk(
  'userDetail/putPatientNotificationSend',
  async (data: any, { dispatch }) => {
    const response = await putPatientNotificationSend(data);
    if (response?.data.success) {
      dispatch(getPatientNotificationsAsync({
        id: data.patientId,
        init: 0,
        size: 10,
        contextKey: data.contextKey
      }));
    }
    return response;
  },
);

export const putPatientNotificationSeenAsync: any | null = createAsyncThunk(
  'userDetail/putPatientNotificationSeen',
  async (data: any, { dispatch }) => {
    const response = await putPatientNotificationSeen(data);
    if (response?.data.success) {
      dispatch(getPatientNotificationsAsync({
        id: data.patientId,
        init: 0,
        size: 10,
        contextKey: data.contextKey,
      }));
    }
    return response;
  },
);

export const putPatientNotificationResponseAsync: any | null = createAsyncThunk(
  'userDetail/putPatientNotificationResponse',
  async (data: any, { dispatch }) => {
    const response = await putPatientNotificationResponse(data, data.params);
    if (response?.data.success) {
      dispatch(getPatientNotificationsAsync({
        id: data.patientId,
        init: 0,
        size: 10,
        contextKey: data.contextKey,
      }));
    }
    return response;
  },
);

export const delPatientNotificationAsync: any | null = createAsyncThunk(
  'userDetail/deletePatientNotification',
  async (data: any) => {
    const response = await deletePatientNotification(data);
    return response;
  },
);

export const getServicesStateAsync: any | null = createAsyncThunk(
  'userDetail/getServicesState',
  async () => {
    const response = await getServicesState();
    return response;
  },
);

export const getHistoricServiceStateAsync: any | null = createAsyncThunk(
  'userDetail/getHistoricServiceState',
  async (data: any, { getState }) => {
    const state: any = getState();
    const response = await getHistoricServiceState(data);
    const allServiceStates: any[] = state.masters.serviceStates?.data ?? [];
    const serviceStateLookup: Record<string, string> = Object.fromEntries(
      allServiceStates.map((s: any) => [s.code, s.label ?? s.code])
    );
    return { response, serviceStateLookup };
  },
);

/**
 * Vuelve a pedir la fecha prevista de baja después de cambiar el estado o el tipo de servicio.
 *
 * El backend la recalcula (o la borra) en esas dos transiciones, así que sin esto la pantalla sigue
 * mostrando la anterior: el bloque de la fecha vive en la misma pestaña que el tipo de servicio, no se
 * desmonta al guardar, y solo se enteraba al recargar. Va con contextKey porque el slice de userDetail es
 * una instancia por paciente/alarma.
 */
const refreshServiceExpiration = (response: any, data: any, dispatch: any) => {
  if (response?.data?.success !== true) return;
  // Los llamantes construyen el id a partir de data?.id || '', asi que puede llegar vacio:
  // sin esta guarda se pediria /patients//service-expiration.
  if (!data?.id) return;
  dispatch(getPatientServiceExpirationAsync({ id: data.id, contextKey: data.contextKey }));
};

export const postServicesStateAsync: any | null = createAsyncThunk(
  'userDetail/postServiceState',
  async (data: any, { dispatch }) => {
    const response = await postServiceState(data.id, data.body);
    refreshServiceExpiration(response, data, dispatch);
    return response;
  },
);

export const putServicesStateAsync: any | null = createAsyncThunk(
  'userDetail/putServiceState',
  async (data: any, { dispatch }) => {
    const response = await putServiceState(data.id, data.body);
    refreshServiceExpiration(response, data, dispatch);
    return response;
  },
);

export const postAddHistoricServiceStateNoteAsync: any | null = createAsyncThunk(
  'userDetail/postAddHistoricServiceStateNote',
  async (data: any, { dispatch }) => {
    const response = await postAddHistoricServiceStateNote(data);
    response?.data.success && dispatch(getHistoricServiceStateAsync({
      id: data.patientId,
      init: 0,
      size: 10,
      contextKey: data.contextKey,
    }));
    return response;
  },
);

export const getServiceConfigurationAsync: any | null = createAsyncThunk(
  'userDetail/getServiceConfiguration',
  async (data: any) => {
    const response = await getServiceConfiguration(data.id);
    return response;
  },
);

export const putServiceConfigurationAsync: any | null = createAsyncThunk(
  'userDetail/putServiceConfiguration',
  async (data: any) => {
    const response = await putServiceConfiguration(data.id, data.serviceId, data.body);
    return response;
  },
);

export const postServiceConfigurationAsync: any | null = createAsyncThunk(
  'userDetail/postServiceConfiguration',
  async (data: any) => {
    const response = await postServiceConfiguration(data.id, data.body);
    return response;
  },
);

export const getServiceContactWayAsync: any | null = createAsyncThunk(
  'userDetail/ServiceContactWayAsync',
  async () => {
    const response = await getServiceContactWay();
    return response;
  },
);

export const putServicesTypeAsync: any | null = createAsyncThunk(
  'userDetail/putServiceTypeState',
  async (data: any, { dispatch }) => {
    const response = await putServiceType(data.id, data.body);
    refreshServiceExpiration(response, data, dispatch);
    return response;
  },
);

export const postServicesTypeAsync: any | null = createAsyncThunk(
  'userDetail/postServiceTypeState',
  async (data: any, { dispatch }) => {
    const response = await postServiceType(data.id, data.body);
    refreshServiceExpiration(response, data, dispatch);
    return response;
  },
);

export const getTermsAndConditionsAgreementAsync: any | null = createAsyncThunk(
  'userDetail/getTermsAndConditionsAgreement',
  async (data: any) => {
    const response = await getTermsAndConditionsAgreement(data);
    return response;
  },
);

export const getPatientMeasuresByTypeAsync: any | null = createAsyncThunk(
  'userDetail/getPatientMeasuresByType',
  async (data: any) => {
    const response = await getPatientMeasuresByType(data.patientId, data.genericTypeId, data.params);
    return response;
  },
);

export const getPatientEffectiveMeasureRulesAsync: any | null = createAsyncThunk(
  'userDetail/getPatientEffectiveMeasureRules',
  async (data: any) => {
    const response = await getPatientEffectiveMeasureRules(data.patientId);
    return response;
  },
  {
    // Varios gráficos pueden montarse a la vez (cards agrupadas): si ya hay una
    // petición en vuelo para este mismo paciente, no se duplica.
    condition: (data: any, { getState }) => {
      const instance = (getState() as any).userDetail?.instances?.[data?.contextKey ?? 'main'];
      const rules = instance?.effectiveMeasureRules;
      return !(rules?.status === 'loading' && String(rules?.patientId) === String(data?.patientId));
    },
  },
);

export const postPatientMeasureNoteAsync: any | null = createAsyncThunk(
  'userDetail/postPatientMeasureNote',
  async (data: any, { getState, dispatch }) => {
    const state: any = getState();
    const user = state.common.user.userLogged;
    const response = await postMeasureNote(data.newNote.referenceId, data.newNote);
    if (response?.data.success) {
      // No refrescamos el listado de eventos aquí: getEventsAsync reescribe
      // dashboard.session, lo que dispara el resetPatientData del contenedor
      // Activity y cerraba el modal de resolución al añadir una nota. El refresco
      // se hace al cerrar el modal (NewResolutionsComponent).
      dispatch(updatePatienteMeasureNotes({
        ...data,
        applicantName: user.access,
      }));
    }
    return response;
  },
);

export const postPatientAddMeasureAsync: any | null = createAsyncThunk(
  'userDetail/postPatientAddMeasure',
  async (data: any) => {
    const response = await postPatientAddMeasure(data);
    return response;
  },
);

export const getVideoCallOpenAsync: any | null = createAsyncThunk(
  'userDetail/getVideoCallOpen',
  async (data: any) => {
    const response = await getVideoCallOpen(data.id);
    return response;
  },
);

export const getVideoCallCloseAsync: any | null = createAsyncThunk(
  'userDetail/getVideoCallClose',
  async (data: any) => {
    const response = await getVideoCallClose(data.id);
    return response;
  },
);

export const postReportAsync: any | null = createAsyncThunk(
  'userDetail/postReport',
  async (data: any) => {
    const response = await postReport(data);
    return response;
  },
);

export const getPatientPersonalDataAsync: any | null = createAsyncThunk(
  'userDetail/getPatientPersonalData',
  async (data: any) => {
    const response = await getPatientPersonalData(getId(data));
    return response;
  },
);

export const getPatientHealthDataAsync: any | null = createAsyncThunk(
  'userDetail/getPatientHealthData',
  async (data: any) => {
    const response = await getPatientHealthData(getId(data));
    return response;
  },
);

export const getPatientInfoDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/getPatientInfoDataByPath',
  async (data: { id: string; path: string; contextKey?: string }) => {
    const response = await getPatientInfoDataByPath(data.id, data.path);
    return response;
  },
);

export const postPatientInfoDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/postPatientInfoDataByPath',
  async (data: any, { dispatch }) => {
    const response = await postPatientInfoDataByPath(data.id, data.path, data.body);
    setTimeout(() => dispatch(getPatientInfoDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 200);
    return response;
  },
);

export const putPatientInfoDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/putPatientInfoDataByPath',
  async (data: any, { dispatch }) => {
    const response = await putPatientInfoDataByPath(data.id, data.path, data.fieldId, data.body);
    setTimeout(() => dispatch(getPatientInfoDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 200);
    return response;
  },
);

export const delPatientInfoDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/delPatientInfoDataByPath',
  async (data: any, { dispatch }) => {
    const response = await delPatientInfoDataByPath(data.id, data.path, data.idPath);
    setTimeout(() => dispatch(getPatientInfoDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 200);
    return response;
  },
);

export const putPatientHealthDataAsync: any | null = createAsyncThunk(
  'userDetail/putPatientHealthData',
  async (data: any, { dispatch }) => {
    const response = await putPatientHealthData(data.patientId, data.path, data.body);
    const dataValue = data.type === 'pathologies' ? data.body.values : data.body.value;
    response?.data.success && dispatch(setUpdateHealthData({ dataValue, type: data.type }));
    return response;
  },
);

export const delPatientHealthDataAsync: any | null = createAsyncThunk(
  'userDetail/delPatientHealthData',
  async (data: any, { dispatch }) => {
    const response = await delPatientHealthData(data.patientId, data.valueId);
    // response?.data.success && dispatch(setDelHealthData({ valueId: data.valueId, type: data.type }));
    dispatch(setDelHealthData({ valueId: data.valueId, type: data.type })); // change to the above
    return response;
  },
);

export const postPatientPersonalDataAsync: any | null = createAsyncThunk(
  'userDetail/postPatientPersonalData',
  async (data: any) => {
    const response = await postPatientPersonalData(data.id, data.body);
    return response;
  },
);

export const putPatientPersonalDataAsync: any | null = createAsyncThunk(
  'userDetail/putPatientPersonalData',
  async (data: any) => {
    const response = await putPatientPersonalData(data.id, data.body);
    return response;
  },
);

export const getPatientHealthInsuranceAsync: any | null = createAsyncThunk(
  'userDetail/getPatientHealthInsurance',
  async (data: any) => {
    const response = await getPatientHealthInsurance(data);
    return response;
  },
);

export const putPatientHealthInsuranceAsync: any | null = createAsyncThunk(
  'userDetail/putPatientHealthInsurance',
  async (data: any) => {
    const response = await putPatientHealthInsurance(data.patientId, data.values);
    return response;
  },
);

export const putRelationshipsPatientAsync: any | null = createAsyncThunk(
  'userDetail/putRelationships',
  async (data: any) => {
    const response = await putRelationships(data.patientId, data.relationId, data);
    return response;
  },
);

export const postRelationshipsPatientAsync: any | null = createAsyncThunk(
  'userDetail/postRelationships',
  async (data: any) => {
    const response = await postRelationships(data.patientId, data);
    return response;
  },
);

export const putDeviceUserAsync: any | null = createAsyncThunk(
  'userDetail/putDeviceUser',
  async (data: any, { dispatch }) => {
    const response = await putDeviceUser(data);
    if (response?.data.success) {
      dispatch(getDeviceGeneralAsync({ id: data.deviceId }));
      dispatch(getHistoricDeviceUsersAsync({ id: data.deviceId, init: 0, size: 10 }));
    }
    return response;
  },
);

export const putUnlinkDeviceUserAsync: any | null = createAsyncThunk(
  'userDetail/putUnlinkDeviceUser',
  async (data: any, { dispatch }) => {
    const response = await putUnlinkDeviceUser(data);
    if (response?.data.success) {
      dispatch(getDeviceGeneralAsync({ id: data.deviceId }));
      dispatch(getHistoricDeviceUsersAsync({ id: data.deviceId, init: 0, size: 10 }));
    }
    return response;
  },
);

export const getHistoricUserDevicesAsync: any | null = createAsyncThunk(
  'userDetail/getHistoricUserDevices',
  async (data: any) => {
    const response = await getHistoricUserDevices(data);
    return response;
  },
);

export const getPatientHealthDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/getPatientHealthDataByPath',
  async (data: any) => {
    const response = await getPatientHealthDataByPath(data.id, data.path);
    return response;
  },
);

export const getPatientHealthServiceAsync: any | null = createAsyncThunk(
  'userDetail/getPatientHealthService',
  async (data: any) => {
    const response = await getPatientHealthService(data.id);
    return response;
  },
);

export const postPatientHealthServiceAsync: any | null = createAsyncThunk(
  'userDetail/postPatientHealthService',
  async (data: any, { dispatch }) => {
    const response = await postPatientHealthService(data.id, data.values);
    setTimeout(() => dispatch(getPatientHealthServiceAsync({ id: data.id, contextKey: data.contextKey })), 300);
    return response;
  },
);

export const putPatientHealthServiceAsync: any | null = createAsyncThunk(
  'userDetail/putPatientHealthService',
  async (data: any, { dispatch }) => {
    const response = await putPatientHealthService(data.id, data.values);
    setTimeout(() => dispatch(getPatientHealthServiceAsync({ id: data.id, contextKey: data.contextKey })), 300);
    return response;
  },
);

export const delPatientHealthServiceAsync: any | null = createAsyncThunk(
  'userDetail/delPatientHealthService',
  async (data: any, { dispatch }) => {
    const response = await delPatientHealthService(data);
    setTimeout(() => dispatch(getPatientHealthServiceAsync({ id: data.patientId, contextKey: data.contextKey })), 300);
    return response;
  },
);

export const postPatientHealthDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/postPatientHealthDataByPath',
  async (data: any, { dispatch }) => {
    const response = await postPatientHealthDataByPath(data.id, data.path, data.body);
    setTimeout(() => dispatch(getPatientHealthDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 300);
    return response;
  },
);

export const putPatientHealthDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/putPatientHealthDataByPath',
  async (data: any, { dispatch }) => {
    const response = await putPatientHealthDataByPath(data.id, data.path, data.fieldId, data.body);
    setTimeout(() => dispatch(getPatientHealthDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 200);
    return response;
  },
);

export const delPatientHealthDataByPathAsync: any | null = createAsyncThunk(
  'userDetail/delPatientHealthDataByPath',
  async (data: any, { dispatch }) => {
    const response = await delPatientHealthDataByPath(data.id, data.path, data.idPath);
    setTimeout(() => dispatch(getPatientHealthDataByPathAsync({ id: data.id, path: data.path, contextKey: data.contextKey })), 200);
    return response;
  },
);

export const getPatientRulesAsync: any | null = createAsyncThunk(
  'userDetail/getPatientRules',
  async (data: any) => {
    const response = await getPatientRules(data.id);
    return response;
  },
);

export const postPatientRulesAsync: any | null = createAsyncThunk(
  'userDetail/postPatientRules',
  async (data: any) => {
    const response = await postPatientRules(data);
    return response;
  },
);

export const putPatientRulesAsync: any | null = createAsyncThunk(
  'userDetail/putPatientRules',
  async (data: any) => {
    const response = await putPatientRules(data);
    return response;
  },
);

export const delPatientRulesAsync: any | null = createAsyncThunk(
  'userDetail/delPatientRules',
  async (data: any) => {
    const response = await delPatientRules(data.id);
    return response;
  },
);

export const getPatientServiceStateOptionsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientServiceStateOptions',
  async (data: any) => {
    const response = await getPatientServiceStateOptions(data.id);
    return response;
  },
);

export const getPatientServiceTypeOptionsAsync: any | null = createAsyncThunk(
  'userDetail/getPatientServiceTypeOptions',
  async (data: any) => {
    try {
      const response = await getPatientServiceTypeOptions(data.id);
      return response;
    } catch (error: any) {
      // 404 = el grupo del paciente y todos sus ancestros están vacíos.
      // Lo tratamos como "sin opciones disponibles" en lugar de error visible.
      if (error?.response?.status === 404) {
        return { data: { items: [] } };
      }
      throw error;
    }
  },
);

// Fecha prevista de baja. Ambos thunks llevan contextKey: el slice de userDetail es una instancia por
// paciente/alarma y un dispatch sin él escribiría en la instancia 'main'.
export const getPatientServiceExpirationAsync: any | null = createAsyncThunk(
  'userDetail/getPatientServiceExpiration',
  async (data: any) => {
    const response = await getPatientServiceExpiration(data.id);
    return response;
  },
);

export const putPatientServiceExpirationAsync: any | null = createAsyncThunk(
  'userDetail/putPatientServiceExpiration',
  async (data: any, { dispatch }) => {
    const response = await putPatientServiceExpiration(data.id, data.body);
    if (response?.data?.success) {
      dispatch(getPatientServiceExpirationAsync({ id: data.id, contextKey: data.contextKey }));
    }
    return response;
  },
);
